import React, { useState } from 'react'
import { useField } from 'formik'
import { Col, Form, InputGroup, Row, Button } from 'react-bootstrap'
import styled from 'styled-components'
import { useRef } from 'react'
import SignatureCanvas from 'react-signature-canvas'

// Custom components
import FieldHelperText from '../form/FieldHelperText'
import RequiredField from '../form/RequiredField'

// Helpers
import { themeColors } from '../../helpers/themeColors'
import { joinClasses } from '../../helpers/utils'

// Assets
import WhiteCheck from '../../assets/img/icons/white-check.svg'
import CloseImg from '../../assets/img/icons/closeIcon.svg'
import PassImg from '../../assets/img/icons/passImg.svg'
import { isEmpty } from 'lodash-es'
import CloseSvg from '../../assets/close'

const colors = themeColors()

const Checkbox = styled(Form.Check)`
  label {
    &::before {
      border-radius: ${({ circular }) =>
        circular === 'true' ? '50% !important' : '0'};
      border: 1px solid #c3cbcd;
      background: #fff;
      width: 1.375rem;
      height: 1.375rem;
      line-height: 32px;
      top: 5px;
    }
    &::after {
      width: 1.375rem;
      height: 1.375rem;
      line-height: 32px;
      top: 5px;
    }
  }
  .custom-control-input {
    z-index: ${({ zindex }) => zindex};

    width: ${({ width }) => width};
    height: ${({ height }) => height};
  }

  .custom-control-input:checked ~ label {
    &::after {
      background: transparent url(${WhiteCheck}) no-repeat center center !important;
    }
  }

  .custom-control-input:checked ~ label[for='checkbox-filters-serviceCenters'] {
    &::before {
      background-color: #fb4e4e !important;
      border-color: #fb4e4e;
    }
    &::after {
      border-color: #fb4e4e;
    }
  }

  .custom-control-input:checked ~ label[for='checkbox-filters-dropBoxes'] {
    ::before {
      background-color: #006593 !important;
      border-color: #006593;
    }
    ::after {
      border-color: #006593;
    }
  }

  .custom-control-input[readonly],
  .custom-control-input[disabled] {
    & ~ label {
      color: ${colors.submarine};
      &::before {
        border-color: transparent;
      }
    }
  }

  .serviceCenters {
    color: #fb4e4e !important;
  }
  .dropBoxes {
    color: #006593;
  }
`

const RadioValve = styled(Form.Check)`
  label {
    &::before {
      border-radius: 50% !important;
      border: ${({ border }) => border};
      background: #ffffff !important;
      width: 1.375rem;
      height: 1.375rem;
      color: ${colors.darkBlue} !important;
      top: 50%;
      transform: translateY(-50%);
    }
    &::after {
      width: 0.75rem;
      height: 0.75rem;
      margin: 0 5px;
      top: 50%;
      transform: translateY(-50%);
    }
  }
  .custom-control-input:checked ~ label {
    &::after {
      background: ${colors.darkBlue} !important;
      border-radius: 50%;
    }
  }
`

const CustomCheckboxes = styled(Form.Group)`
  .invalid-feedback {
    display: none;
  }
`

const TriggerButton = styled.button`
  border: ${({ border }) => border};
  background: none;
  box-sizing: border-box;
  font-family: Open Sans;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  display: flex;
  align-items: center;
  text-align: center;
  letter-spacing: 0.15px;
  padding: 11px 32px;
  color: ${({ color }) => color};
  min-width: ${({ minwidth }) => minwidth};
  justify-content: space-between;
`

const SimpleUploadBtn = styled.div`
  display: flex;
  align-items: center;
  label {
    color: ${colors.darkBlue} !important;
    margin-bottom: 0;
    &:hover {
      color: white !important;
    }
  }
  h4 {
    margin-left: 40px;
  }
`

export const FormikTextField = ({
  label,
  classes,
  fieldRef,
  handleBlur,
  handleChange,
  labelClassname,
  formGroupClasses,
  isRequired,
  noLabel,
  ...props
}) => {
  const [field, meta, helpers] = useField(props)

  if (handleBlur) {
    field.onBlur = (e) => handleBlur(e, helpers, props.name)
  }

  if (handleChange) {
    field.onChange = (e) => {
      const fieldVal = e.target.value
      helpers.setValue(fieldVal)
      handleChange(fieldVal)
    }
  }

  const FormLabel = <Form.Label className={labelClassname}>{label}</Form.Label>
  const RequiredLabel = <RequiredField>{FormLabel}</RequiredField>

  return (
    <Form.Group id={`form-group-${props.name}`} className={formGroupClasses}>
      {isRequired && !noLabel ? RequiredLabel : !noLabel && FormLabel}
      <Form.Control
        isInvalid={meta.touched && meta.error}
        className={classes}
        ref={fieldRef || null}
        {...field}
        {...props}
      />
      {meta.touched && !!meta.error && (
        <Form.Control.Feedback type="invalid">
          {meta.error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  )
}

export const FormikTextFieldRemovable = ({
  label,
  classes,
  fieldRef,
  handleBlur,
  labelClassname,
  formGroupClasses,
  isRequired,
  noLabel,
  onRemove = () => {},
  fieldsLength,
  ...props
}) => {
  const [field, meta, helpers] = useField(props)

  if (handleBlur) {
    field.onBlur = (e) => handleBlur(e, helpers, props.name)
  }

  const FormLabel = <Form.Label className={labelClassname}>{label}</Form.Label>
  const RequiredLabel = <RequiredField>{FormLabel}</RequiredField>

  const responsivenessControll = {
    input: {
      md: fieldsLength > 1 ? 11 : 12,
      xs: fieldsLength > 1 ? 11 : 12,
    },
    removeButton: {
      md: 1,
      xs: 1,
    },
  }

  return (
    <Form.Group id={`form-group-${props.name}`} className={formGroupClasses}>
      {isRequired && !noLabel ? RequiredLabel : !noLabel && FormLabel}
      <Row>
        <Col {...responsivenessControll.input}>
          <Form.Control
            isInvalid={meta.touched && meta.error}
            className={classes}
            ref={fieldRef || null}
            {...field}
            {...props}
          />
        </Col>
        {fieldsLength > 1 && (
          <Col
            {...responsivenessControll.removeButton}
            className="pl-0 d-flex align-items-center justify-content-center"
            onClick={onRemove}
            style={{
              cursor: 'pointer',
            }}
          >
            <CloseSvg />
          </Col>
        )}
      </Row>

      <ErrorText className="mt-2">
        {meta.touched && meta.error && meta.error}
      </ErrorText>
    </Form.Group>
  )
}

export const FormikInputGroup = ({
  label,
  classes,
  append,
  prepend,
  fieldRef,
  helperText,
  inputGroupClasses,
  formGroupClasses,
  showValidationIconAppend,
  labelClassname,
  isRequired,
  noLabel,
  handleBlur,
  handleChange,
  ...props
}) => {
  const [field, meta, helpers] = useField(props)
  const [focused, setFocused] = useState(false)

  if (handleBlur) {
    field.onBlur = (e) => {
      handleBlur(e, helpers)
      setFocused(false)
    }
  } else {
    field.onBlur = (e) => {
      helpers.setTouched(true)
      setFocused(false)
    }
  }

  if (handleChange) {
    field.onChange = (e) => {
      const fieldVal = e.target.value
      helpers.setValue(fieldVal)
      handleChange(fieldVal)
    }
  }

  const isInvalid = meta.touched && meta.error
  const isValid = meta.touched && !meta.error && !isEmpty(meta.value)

  const FormLabel = <Form.Label className={labelClassname}>{label}</Form.Label>
  const RequiredLabel = <RequiredField>{FormLabel}</RequiredField>

  return (
    <Form.Group id={`form-group-${props.name}`} className={formGroupClasses}>
      {isRequired && !noLabel ? RequiredLabel : !noLabel && FormLabel}
      <InputGroup
        className={joinClasses([
          'input-group-merge',
          showValidationIconAppend && (isInvalid || isValid) ? 'appended' : '',
          inputGroupClasses,
          focused ? 'is-focused' : '',
          isInvalid ? 'is-invalid' : '',
        ])}
      >
        {prepend}
        <Form.Control
          isInvalid={isInvalid}
          className={classes}
          ref={fieldRef || null}
          onFocus={() => setFocused(true)}
          {...field}
          {...props}
        />
        {append}
        {(isInvalid || isValid) && showValidationIconAppend && (
          <InputGroup.Append>
            <InputGroup.Text className="px-18px">
              <img src={isInvalid ? CloseImg : PassImg} alt="validation-icon" />
            </InputGroup.Text>
          </InputGroup.Append>
        )}
        {meta.touched && !!meta.error ? (
          <Form.Control.Feedback as="span" type="invalid">
            {meta.error}
          </Form.Control.Feedback>
        ) : (
          <>{helperText && <FieldHelperText>{helperText}</FieldHelperText>}</>
        )}
      </InputGroup>
    </Form.Group>
  )
}

export const FormikCustomCheckbox = ({
  label,
  classes,
  formGroupClasses,
  labelClasses,
  addLabelSpace,
  handleChange,
  ...props
}) => {
  const [field, meta, helpers] = useField(props)

  
  if (handleChange) {
    field.onChange = () => {
      const newValue = !field.value
      helpers.setValue(newValue)
      handleChange(newValue)
    }
  }

  return (
    <Form.Group id={`form-group-${props.name}`} className={formGroupClasses}>
      {addLabelSpace && <Form.Label />}
      <Checkbox
        width={props.newaddress ? '1.5rem !important' : ''}
        height={props.newaddress ? '1.8rem !important' : ''}
        zindex={props.newaddress ? '1 !important' : ''}
        id={`checkbox-${props.name}`}
        custom
        type="checkbox"
        isInvalid={meta.touched && meta.error}
        className={classes}
        feedback={meta.error}
        label={
          <span className={joinClasses(['ml-3', labelClasses])}>{label}</span>
        }
        checked={field.value}
        {...field}
        {...props}
      />
    </Form.Group>
  )
}

export const FormikCustomRadios = ({
  label,
  classes,
  options,
  labelClasses,
  formGroupClasses,
  optionClasses,
  fromAddressBook,
  fromaddressbook,
  ...props
}) => {
  const [field, meta] = useField(props)
  return (
    <Form.Group id={`form-group-${props.name}`} className={formGroupClasses}>
      {label && <Form.Label className={labelClasses}>{label}</Form.Label>}
      {options &&
        options.map((opt) => (
          <RadioValve
            border={
              fromAddressBook || fromaddressbook
                ? '1px solid #737b7d'
                : `1px solid ${colors.darkBlue}`
            }
            id={`radio-${props.name}-${opt.value}`}
            key={opt.value}
            custom
            type="radio"
            isInvalid={meta.touched && meta.error}
            className={classes}
            {...field}
            {...props}
            value={opt.value}
            label={
              <span className={joinClasses(['ml-3', optionClasses])}>
                {opt.label}
              </span>
            }
          />
        ))}
      {meta.touched && !!meta.error && (
        <div className="text-danger text-body-smallest">{meta.error}</div>
      )}
    </Form.Group>
  )
}

export const FormikFileSelect = ({
  label,
  classes,
  handleBlur,
  labelClassname,
  formGroupClasses,
  isRequired,
  fromAddressBook,
  ...props
}) => {
  const [field, meta, helpers] = useField(props)

  if (handleBlur) {
    field.onBlur = (e) => handleBlur(e, helpers, props.name)
  }

  const fileInputRef = useRef()

  const handleLabelClick = () => {
    fileInputRef.current.click()
  }

  const FormLabel = !props.wtable && (
    <Form.Label className={labelClassname}>{label}</Form.Label>
  )
  const RequiredLabel = <RequiredField>{FormLabel}</RequiredField>

  return (
    <Form.Group id={`form-group-${props.name}`} className={formGroupClasses}>
      {!props.wtable && isRequired ? RequiredLabel : FormLabel}
      <Row>
        <Col
          md={
            props.fromAddressBook || fromAddressBook ? 8 : props.wtable ? 12 : 2
          }
        >
          <TriggerButton
            onClick={handleLabelClick}
            minwidth={props.fromAddressBook || fromAddressBook ? '410px' : ''}
            border={
              props.fromAddressBook ? '2px solid #AAAAAA' : '3px solid #006593'
            }
            color={
              props.fromAddressBook || fromAddressBook
                ? '1px solid #111111'
                : '#006593'
            }
          >
            {props.wtable && props.import
              ? 'Import address'
              : props.wtable && props.export
              ? 'Export address'
              : 'Choose file'}

            {props.fromAddressBook ||
              (fromAddressBook && <span className="fe fe-chevron-down"></span>)}
          </TriggerButton>
          <Form.File
            hidden
            isInvalid={meta.touched && meta.error}
            className={classes}
            ref={fileInputRef}
            {...field}
            {...props}
          ></Form.File>
        </Col>
      </Row>
      {meta.touched && !!meta.error && (
        <Form.Control.Feedback type="invalid">
          {meta.error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  )
}

export const FormikSignPad = ({
  label,
  classes,
  fieldRef,
  handleBlur,
  labelClassname,
  formGroupClasses,
  onEnd = () => {},
  isRequired,
  ...props
}) => {
  // eslint-disable-next-line no-unused-vars
  const [field, meta, { setValue }] = useField(props)
  const FormLabel = <Form.Label className={labelClassname}>{label}</Form.Label>
  const RequiredLabel = <RequiredField>{FormLabel}</RequiredField>

  return (
    <Form.Group id={`form-group-${props.name}`} className={formGroupClasses}>
      <Row>
        <Col>{isRequired ? RequiredLabel : FormLabel}</Col>
      </Row>
      <SignatureCanvas
        penColor="gray"
        canvasProps={{ width: 500, height: 94, className: 'sigCanvas' }}
        ref={fieldRef}
        onEnd={onEnd}
      />
      {meta.touched && !!meta.error && (
        <Form.Control.Feedback type="invalid">
          {meta.error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  )
}

const InputGroupStyle = styled(Row)`
  input {
    border: none;
  }
`

const ErrorText = styled.span`
  color: #fb4e4e;
  font-size: 14px;
`

const FormControlStyled = styled(Form.Control)`
  @media screen and (max-width: 767px) {
    font-size: 14px;
    padding: 0;
  }
`

const TrackButton = styled(Button)`
  @media screen and (max-width: 767px) {
    font-size: 12px;
    padding-right: 0.5rem;
  }
`

export const FormikTextFieldWithButton = ({
  label,
  classes,
  fieldRef,
  handleBlur,
  labelClassname,
  formGroupClasses,
  isRequired,
  noLabel,
  onButtonClick = () => {
    /**
     * @func event when button is clicked
     */
  },
  ...props
}) => {
  const [field, meta, helpers] = useField(props.name)

  if (handleBlur) {
    field.onBlur = (e) => handleBlur(e, helpers, props.name)
  }

  return (
    <Form.Group id={`form-group-${props.name}`} className={formGroupClasses}>
      {/* {isRequired && !noLabel ? RequiredLabel : !noLabel && FormLabel} */}
      <Row>
        <Col className="pl-0">
          <h3>{label}</h3>
        </Col>
      </Row>
      <InputGroupStyle className="p-0 d-flex align-items-center bg-white">
        <Col md={9} xs={9}>
          <FormControlStyled
            isInvalid={meta.touched && meta.error}
            className={classes}
            ref={fieldRef || null}
            {...field}
            {...props}
          />
        </Col>
        <Col md={3} xs={3} className="d-flex justify-content-end pr-2">
          <TrackButton className="btn-slant" onClick={onButtonClick}>
            Track
          </TrackButton>
        </Col>
      </InputGroupStyle>
      <Row>
        <ErrorText className="mt-2">{meta.error && meta.error}</ErrorText>
      </Row>
    </Form.Group>
  )
}

const StyledColumnCheckbox = styled(Col)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`

const StyledColumnLabel = styled(Col)`
  display: flex;
  align-items: center;

  .form-label {
    font-size: 13px;
    line-height: 15px;
  }
`

export const FormikNormalCheckBox = ({
  label,
  classes,
  formGroupClasses,
  labelClasses,
  ...props
}) => {
  const [field, , { setValue }] = useField(props)

  return (
    <Form.Group id={`form-group-${props.name}`} className="mb-0">
      <Row className="mb-0">
        <StyledColumnCheckbox xs={3} md={3}>
          <Form.Check
            id={`checkbox-${props.name}`}
            className={classes}
            checked={field.value}
            {...field}
            {...props}
            onChange={(e) => {
              setValue(e.target.checked)
            }}
          />
        </StyledColumnCheckbox>
        <StyledColumnLabel xs={9} md={9}>
          <Form.Label>{label}</Form.Label>
        </StyledColumnLabel>
      </Row>
    </Form.Group>
  )
}

export const FormikCustomCheckboxes = ({
  label,
  classes,
  options,
  labelClasses,
  formGroupClasses,
  optionClasses,
  isInline,
  ...props
}) => {
  const [field, meta] = useField(props)

  return (
    <CustomCheckboxes
      id={`form-group-${props.name}`}
      className={formGroupClasses}
    >
      {label && <Form.Label className={labelClasses}>{label}</Form.Label>}
      {options &&
        options.map((opt) => (
          <Checkbox
            id={`checkbox-${props.name}-${opt.value}`}
            key={opt.value}
            custom
            type="checkbox"
            isInvalid={meta.touched && meta.error}
            className={classes}
            {...field}
            {...props}
            value={opt.value}
            label={
              <span className={joinClasses(['ml-3', optionClasses, opt.value])}>
                {opt.label}
              </span>
            }
            inline={isInline}
            circular="true"
          />
        ))}
      {meta.touched && !!meta.error && (
        <div className="text-danger text-body-smallest">{meta.error}</div>
      )}
    </CustomCheckboxes>
  )
}

export const FormikSimpleFileSelect = ({
  label,
  classes,
  handleBlur,
  labelClassname,
  formGroupClasses,
  isRequired,
  ...props
}) => {
  const [field, meta, helpers] = useField(props)

  if (handleBlur) {
    field.onBlur = (e) => handleBlur(e, helpers, props.name)
  }

  const handleChange = (e) => {
    if (e.target.files.length) {
      helpers.setValue(e.currentTarget.files[0])
      if (props.handleChange) {
        props.handleChange(props.fieldToSave, e.target.files[0])
      }
    }
  }

  const FormLabel = !props.wtable && (
    <Form.Label className={labelClassname}>{label}</Form.Label>
  )
  const RequiredLabel = <RequiredField>{FormLabel}</RequiredField>

  return (
    <Form.Group id={`form-group-${props.name}`} className={formGroupClasses}>
      {!props.wtable && isRequired ? RequiredLabel : FormLabel}
      <SimpleUploadBtn
        className={joinClasses([meta.touched && meta.error && 'is-invalid'])}
      >
        <label
          htmlFor={props.id}
          role="button"
          className={`btn ${
            meta.touched && meta.error
              ? 'btn-outline-danger'
              : 'btn-outline-primary'
          } thick-outline`}
        >
          Choose File
        </label>
        <input
          id={props.id}
          hidden
          type="file"
          name={props.name}
          onChange={handleChange}
          className="form-control"
        />
        {field.value && <h4 className="file-selected">{field.value.name}</h4>}
      </SimpleUploadBtn>
      {meta.touched && !!meta.error && (
        <Form.Control.Feedback type="invalid">
          {meta.error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  )
}
